/**
 * DevExtreme (esm/ui/html_editor/utils/image_uploader_helper.js)
 * Version: 22.1.4
 * Build date: Fri Jul 22 2022
 *
 * Copyright (c) 2012 - 2022 Developer Express Inc. ALL RIGHTS RESERVED
 * Read about DevExtreme licensing here: https://js.devexpress.com/Licensing/
 */
import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
var _excluded = ["imageSrc", "src"];
import $ from "../../../core/renderer";
import localizationMessage from "../../../localization/message";
import {
    map
} from "../../../core/utils/iterator";
import {
    extend
} from "../../../core/utils/extend";
import {
    getHeight,
    getWidth
} from "../../../core/utils/size";
import devices from "../../../core/devices";
import {
    isDefined
} from "../../../core/utils/type";
var isMobile = "phone" === devices.current().deviceType;
var DIALOG_IMAGE_CAPTION = "dxHtmlEditor-dialogImageCaption";
var DIALOG_UPDATE_IMAGE_CAPTION = "dxHtmlEditor-dialogUpdateImageCaption";
var DIALOG_IMAGE_FIELD_URL = "dxHtmlEditor-dialogImageUrlField";
var DIALOG_IMAGE_FIELD_ALT = "dxHtmlEditor-dialogImageAltField";
var DIALOG_IMAGE_FIELD_WIDTH = "dxHtmlEditor-dialogImageWidthField";
var DIALOG_IMAGE_FIELD_HEIGHT = "dxHtmlEditor-dialogImageHeightField";
var DIALOG_IMAGE_ADD_BUTTON = "dxHtmlEditor-dialogImageAddButton";
var DIALOG_IMAGE_UPDATE_BUTTON = "dxHtmlEditor-dialogImageUpdateButton";
var DIALOG_IMAGE_SPECIFY_URL = "dxHtmlEditor-dialogImageSpecifyUrl";
var DIALOG_IMAGE_SELECT_FILE = "dxHtmlEditor-dialogImageSelectFile";
var DIALOG_IMAGE_KEEP_ASPECT_RATIO = "dxHtmlEditor-dialogImageKeepAspectRatio";
var DIALOG_IMAGE_ENCODE_TO_BASE64 = "dxHtmlEditor-dialogImageEncodeToBase64";
var DIALOG_IMAGE_POPUP_CLASS = "dx-htmleditor-add-image-popup";
var DIALOG_IMAGE_POPUP_WITH_TABS_CLASS = "dx-htmleditor-add-image-popup-with-tabs";
var DIALOG_IMAGE_FIX_RATIO_CONTAINER = "dx-fix-ratio-container";
var FORM_DIALOG_CLASS = "dx-formdialog";
var USER_ACTION = "user";
var SILENT_ACTION = "silent";
var FILE_UPLOADER_NAME = "dx-htmleditor-image";
import ButtonGroup from "../../button_group";
import FileUploader from "../../file_uploader";
import TextBox from "../../text_box";
export class ImageUploader {
    constructor(module, config) {
        this.module = module;
        this.config = null !== config && void 0 !== config ? config : {};
        this.quill = this.module.quill;
        this.editorInstance = this.module.editorInstance
    }
    render() {
        var _this$config$tabs;
        this.tabPanelIndex = 0;
        this.formData = this.getFormData();
        this.isUpdating = this.isImageUpdating();
        this.actualTabs = null === (_this$config$tabs = this.config.tabs) || void 0 === _this$config$tabs ? void 0 : _this$config$tabs.slice();
        this.tabs = this.createTabs(this.formData);
        var formConfig = this.getFormConfig();
        this.modifyDialogPopupOptions();
        this.editorInstance.showFormDialog(formConfig).done((formData, event) => {
            this.tabs[this.getActiveTabIndex()].strategy.pasteImage(formData, event)
        }).always(() => {
            this.resetDialogPopupOptions();
            this.quill.focus()
        })
    }
    getActiveTabIndex() {
        return this.isUpdating ? 0 : this.tabPanelIndex
    }
    getFormData() {
        return this.getUpdateDialogFormData(this.quill.getFormat())
    }
    getUpdateDialogFormData(formData) {
        var {
            imageSrc: imageSrc,
            src: src
        } = formData, props = _objectWithoutPropertiesLoose(formData, _excluded);
        return _extends({
            src: null !== imageSrc && void 0 !== imageSrc ? imageSrc : src
        }, props)
    }
    createTabs(formData) {
        var result = [];
        if (!this.actualTabs || this.isUpdating) {
            this.actualTabs = ["url"]
        }
        this.actualTabs = this.normalizeTabs(this.actualTabs);
        this.actualTabs.forEach(tabName => {
            var newTab = "url" === tabName ? new UrlTab(this.module, {
                config: this.config,
                formData: formData,
                isUpdating: this.isUpdating
            }) : new FileTab(this.module, {
                config: this.config
            });
            result.push(newTab)
        });
        return result
    }
    normalizeTabs(tabsConfig) {
        return tabsConfig.map(item => "object" === typeof item ? item.name : item)
    }
    isImageUpdating() {
        var _this$module$quill$ge;
        return Object.prototype.hasOwnProperty.call(null !== (_this$module$quill$ge = this.module.quill.getFormat()) && void 0 !== _this$module$quill$ge ? _this$module$quill$ge : {}, "imageSrc")
    }
    modifyDialogPopupOptions() {
        var wrapperClasses = "".concat(DIALOG_IMAGE_POPUP_CLASS, " ").concat(FORM_DIALOG_CLASS);
        if (this.useTabbedItems()) {
            wrapperClasses += " ".concat(DIALOG_IMAGE_POPUP_WITH_TABS_CLASS)
        }
        this.editorInstance.formDialogOption({
            title: localizationMessage.format(this.isUpdating ? DIALOG_UPDATE_IMAGE_CAPTION : DIALOG_IMAGE_CAPTION),
            "toolbarItems[0].options.text": localizationMessage.format(this.isUpdating ? DIALOG_IMAGE_UPDATE_BUTTON : DIALOG_IMAGE_ADD_BUTTON),
            "toolbarItems[0].options.visible": !this.shouldHideAddButton(),
            wrapperAttr: {
                class: wrapperClasses
            }
        })
    }
    shouldHideAddButton() {
        return !this.isUpdating && 1 === this.actualTabs.length && "url" !== this.actualTabs[0]
    }
    resetDialogPopupOptions() {
        this.editorInstance.formDialogOption({
            "toolbarItems[0].options.text": localizationMessage.format("OK"),
            "toolbarItems[0].options.visible": true,
            wrapperAttr: {
                class: FORM_DIALOG_CLASS
            }
        })
    }
    useTabbedItems() {
        return this.actualTabs.length > 1
    }
    getFormWidth() {
        return isMobile ? "100%" : 493
    }
    getFormConfig() {
        return {
            formData: this.formData,
            width: this.getFormWidth(),
            labelLocation: "top",
            colCount: this.useTabbedItems() ? 1 : 11,
            items: this.getItemsConfig()
        }
    }
    getItemsConfig() {
        var config = {};
        if (this.useTabbedItems()) {
            var tabsConfig = map(this.tabs, tabController => ({
                title: tabController.getTabName(),
                colCount: 11,
                items: tabController.getItemsConfig()
            }));
            config = [{
                itemType: "tabbed",
                tabPanelOptions: {
                    onSelectionChanged: e => {
                        this.tabPanelIndex = e.component.option("selectedIndex")
                    }
                },
                tabs: tabsConfig
            }]
        } else {
            config = this.tabs[0].getItemsConfig()
        }
        return config
    }
}
class BaseTab {
    constructor(module, _ref) {
        var {
            config: config,
            formData: formData,
            isUpdating: isUpdating
        } = _ref;
        this.module = module;
        this.config = config;
        this.formData = formData;
        this.isUpdating = isUpdating;
        this.strategy = this.getStrategy()
    }
    getItemsConfig() {
        return this.strategy.getItemsConfig()
    }
}
class UrlTab extends BaseTab {
    getTabName() {
        return localizationMessage.format(DIALOG_IMAGE_SPECIFY_URL)
    }
    getStrategy() {
        return this.isUpdating ? new UpdateUrlStrategy(this.module, this.config, this.formData) : new AddUrlStrategy(this.module, this.config)
    }
}
class FileTab extends BaseTab {
    getTabName() {
        return localizationMessage.format(DIALOG_IMAGE_SELECT_FILE)
    }
    getStrategy() {
        return new FileStrategy(this.module, this.config)
    }
}
class BaseStrategy {
    constructor(module, config) {
        this.module = module;
        this.config = config;
        this.editorInstance = module.editorInstance;
        this.quill = module.quill;
        this.selection = this.getQuillSelection()
    }
    getQuillSelection() {
        var selection = this.quill.getSelection();
        return null !== selection && void 0 !== selection ? selection : {
            index: this.quill.getLength(),
            length: 0
        }
    }
    pasteImage() {}
}
class AddUrlStrategy extends BaseStrategy {
    constructor(module, config) {
        super(module, config);
        this.shouldKeepAspectRatio = true
    }
    pasteImage(formData, event) {
        this.module.saveValueChangeEvent(event);
        urlUpload(this.quill, this.selection.index, formData)
    }
    keepAspectRatio(data, _ref2) {
        var {
            dependentEditor: dependentEditor,
            e: e
        } = _ref2;
        var newValue = parseInt(e.value);
        var previousValue = parseInt(e.previousValue);
        var previousDependentEditorValue = parseInt(dependentEditor.option("value"));
        data.component.updateData(data.dataField, newValue);
        if (this.shouldKeepAspectRatio && previousDependentEditorValue && previousValue && !this.preventRecalculating) {
            this.preventRecalculating = true;
            dependentEditor.option("value", Math.round(newValue * previousDependentEditorValue / parseInt(previousValue)).toString())
        }
        this.preventRecalculating = false
    }
    createKeepAspectRatioEditor($container, data, dependentEditorDataField) {
        return this.editorInstance._createComponent($container, TextBox, extend(true, data.editorOptions, {
            value: data.component.option("formData")[data.dataField],
            onEnterKey: data.component.option("onEditorEnterKey").bind(this.editorInstance._formDialog, data),
            onValueChanged: e => {
                this.keepAspectRatio(data, {
                    dependentEditor: this[dependentEditorDataField + "Editor"],
                    e: e
                })
            }
        }))
    }
    getItemsConfig() {
        return [{
            dataField: "src",
            colSpan: 11,
            label: {
                text: localizationMessage.format(DIALOG_IMAGE_FIELD_URL)
            }
        }, {
            dataField: "width",
            colSpan: 6,
            label: {
                text: localizationMessage.format(DIALOG_IMAGE_FIELD_WIDTH)
            },
            template: data => {
                var $content = $("<div>").addClass(DIALOG_IMAGE_FIX_RATIO_CONTAINER);
                var $widthEditor = $("<div>").appendTo($content);
                this.widthEditor = this.createKeepAspectRatioEditor($widthEditor, data, "height");
                var $ratioEditor = $("<div>").appendTo($content);
                this.editorInstance._createComponent($ratioEditor, ButtonGroup, {
                    items: [{
                        icon: "imgarlock",
                        value: "keepRatio"
                    }],
                    hint: localizationMessage.format(DIALOG_IMAGE_KEEP_ASPECT_RATIO),
                    focusStateEnabled: false,
                    keyExpr: "value",
                    stylingMode: "outlined",
                    selectionMode: "multiple",
                    selectedItemKeys: ["keepRatio"],
                    onSelectionChanged: e => {
                        this.shouldKeepAspectRatio = !!e.component.option("selectedItems").length
                    }
                });
                return $content
            }
        }, {
            dataField: "height",
            colSpan: 5,
            label: {
                text: localizationMessage.format(DIALOG_IMAGE_FIELD_HEIGHT)
            },
            template: data => {
                var $content = $("<div>");
                this.heightEditor = this.createKeepAspectRatioEditor($content, data, "width");
                return $content
            }
        }, {
            dataField: "alt",
            colSpan: 11,
            label: {
                text: localizationMessage.format(DIALOG_IMAGE_FIELD_ALT)
            }
        }]
    }
}
class UpdateUrlStrategy extends AddUrlStrategy {
    constructor(module, config, formData) {
        super(module, config);
        this.formData = formData;
        this.modifyFormData()
    }
    modifyFormData() {
        var {
            imageSrc: imageSrc
        } = this.quill.getFormat(this.selection.index - 1, 1);
        if (!imageSrc || 0 === this.selection.index) {
            this.selection = {
                index: this.selection.index + 1,
                length: 0
            };
            this.quill.setSelection(this.selection.index, this.selection.length, SILENT_ACTION)
        }
        var imgElement = this.quill.getLeaf(this.selection.index)[0].domNode;
        if (imgElement) {
            var _this$formData$width, _this$formData$height;
            this.formData.width = null !== (_this$formData$width = this.formData.width) && void 0 !== _this$formData$width ? _this$formData$width : getWidth($(imgElement));
            this.formData.height = null !== (_this$formData$height = this.formData.height) && void 0 !== _this$formData$height ? _this$formData$height : getHeight($(imgElement))
        }
    }
    pasteImage(formData, event) {
        this.quill.deleteText(this.embedFormatIndex(), 1, SILENT_ACTION);
        this.selection.index -= 1;
        super.pasteImage(formData, event)
    }
    embedFormatIndex() {
        var _this$selection;
        var selection = null !== (_this$selection = this.selection) && void 0 !== _this$selection ? _this$selection : this.quill.getSelection();
        if (selection) {
            if (selection.length) {
                return selection.index
            } else {
                return selection.index - 1
            }
        } else {
            return this.quill.getLength()
        }
    }
}
class FileStrategy extends BaseStrategy {
    constructor(module, config) {
        super(module, config);
        this.useBase64 = !isDefined(this.config.fileUploadMode) || "base64" === this.config.fileUploadMode
    }
    closeDialogPopup(data) {
        this.editorInstance._formDialog.hide({
            file: data.value ? data.value[0] : data.file
        }, data.event)
    }
    serverUpload(data) {
        if (!this.useBase64) {
            var imageUrl = correctSlashesInUrl(this.config.uploadDirectory) + data.file.name;
            urlUpload(this.quill, this.selection.index, {
                src: imageUrl
            });
            this.closeDialogPopup(data)
        }
    }
    base64Upload(data) {
        this.quill.getModule("uploader").upload(this.selection, data.value, true);
        this.closeDialogPopup(data)
    }
    pasteImage(formData, event) {
        if (this.useBase64) {
            super.pasteImage(formData, event)
        }
    }
    isBase64Editable() {
        return "both" === this.config.fileUploadMode
    }
    getFileUploaderOptions() {
        var fileUploaderOptions = {
            uploadUrl: this.config.uploadUrl,
            onValueChanged: data => {
                if (this.useBase64) {
                    this.base64Upload(data)
                } else if (data.value.length) {
                    data.component.upload()
                }
            },
            onUploaded: data => {
                this.serverUpload(data)
            }
        };
        return extend({}, getFileUploaderBaseOptions(), fileUploaderOptions, this.config.fileUploaderOptions)
    }
    getItemsConfig() {
        return [{
            itemType: "simple",
            dataField: "files",
            colSpan: 11,
            label: {
                visible: false
            },
            template: () => {
                var $content = $("<div>");
                this.module.editorInstance._createComponent($content, FileUploader, this.getFileUploaderOptions());
                return $content
            }
        }, {
            itemType: "simple",
            colSpan: 11,
            label: {
                visible: false
            },
            editorType: "dxCheckBox",
            editorOptions: {
                value: this.useBase64,
                visible: this.isBase64Editable(),
                text: localizationMessage.format(DIALOG_IMAGE_ENCODE_TO_BASE64),
                onValueChanged: e => {
                    if (this.isBase64Editable()) {
                        this.useBase64 = e.value
                    }
                }
            }
        }]
    }
}
export function correctSlashesInUrl(url) {
    return "/" !== url[url.length - 1] ? url + "/" : url
}
export function getFileUploaderBaseOptions() {
    return {
        value: [],
        name: FILE_UPLOADER_NAME,
        accept: "image/*",
        uploadMode: "useButtons"
    }
}
export function urlUpload(quill, index, attributes) {
    quill.insertEmbed(index, "extendedImage", attributes, USER_ACTION);
    quill.setSelection(index + 1, 0, USER_ACTION)
}
